import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService';
import { useAuth } from '../../context/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ConfirmDialog from '../Common/ConfirmDialog';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles - compact
const headerCellStyle = {
  backgroundColor: '#1565C0',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  borderBottom: '2px solid #0D47A1',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
  padding: '8px 6px',
  whiteSpace: 'nowrap',
};

// Compact cell style
const compactCellStyle = {
  fontSize: '0.8rem',
  padding: '6px 6px',
};

const ProjectList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Filter state
  const [filterProjectCode, setFilterProjectCode] = useState('');
  const [filterProjectName, setFilterProjectName] = useState('');
  const [filterPD, setFilterPD] = useState('');
  const [filterPM, setFilterPM] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  // Check if user can edit/delete (not User role)
  const canEdit = user?.role !== 'User';
  const canDelete = user?.role === 'Admin' || user?.role === 'SuperUser';

  // Compute unique filter options (LOVs)
  const filterOptions = useMemo(() => {
    const projectCodes = [...new Set(projects.map(p => p.project_code))].sort();
    const projectNames = [...new Set(projects.map(p => p.name))].sort();

    // Extract individual PD names (split by comma if multiple)
    const pdNames = [...new Set(
      projects
        .flatMap(p => (p.pd_name || '').split(',').map(n => n.trim()))
        .filter(n => n && n !== '-')
    )].sort();

    // Extract individual PM names (split by comma if multiple)
    const pmNames = [...new Set(
      projects
        .flatMap(p => (p.pm_name || '').split(',').map(n => n.trim()))
        .filter(n => n && n !== '-')
    )].sort();

    return { projectCodes, projectNames, pdNames, pmNames };
  }, [projects]);

  useEffect(() => {
    loadProjects();
  }, []);

  // Filter effect
  useEffect(() => {
    let filtered = [...projects];

    if (filterProjectCode) {
      filtered = filtered.filter(p =>
        p.project_code.toLowerCase().includes(filterProjectCode.toLowerCase())
      );
    }
    if (filterProjectName) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(filterProjectName.toLowerCase())
      );
    }
    if (filterPD) {
      filtered = filtered.filter(p =>
        (p.pd_name || '').toLowerCase().includes(filterPD.toLowerCase())
      );
    }
    if (filterPM) {
      filtered = filtered.filter(p =>
        (p.pm_name || '').toLowerCase().includes(filterPM.toLowerCase())
      );
    }

    setFilteredProjects(filtered);
  }, [projects, filterProjectCode, filterProjectName, filterPD, filterPM]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProject = () => {
    navigate('/projects/new');
  };

  const handleEditProject = (code) => {
    navigate(`/projects/edit/${code}`);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    try {
      await projectService.delete(projectToDelete.project_code);
      setSuccess('Project deleted successfully');
      loadProjects();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete project');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!filteredProjects || filteredProjects.length === 0) return;

    const headers = ['SNo', 'Project Code', 'Project Name', 'PD', 'PM', 'Start Date', 'End Date', 'Total SOW', 'Burn Value', 'Remaining'];

    const dataRows = filteredProjects.map((project, index) => [
      index + 1,
      project.project_code,
      project.name,
      project.pd_name || '-',
      project.pm_name || '-',
      project.sow_start_date || 'Not set',
      project.sow_end_date || 'Ongoing',
      project.total_sow_value || 0,
      project.burn_value || 0,
      project.remaining_sow_value || 0,
    ]);

    // Calculate totals
    const totalSOW = filteredProjects.reduce((sum, p) => sum + (p.total_sow_value || 0), 0);
    const totalBurn = filteredProjects.reduce((sum, p) => sum + (p.burn_value || 0), 0);
    const totalRemaining = filteredProjects.reduce((sum, p) => sum + (p.remaining_sow_value || 0), 0);

    // Add totals row
    const totalsRow = ['', 'TOTAL', '', '', '', '', '', totalSOW, totalBurn, totalRemaining];

    const wsData = [headers, ...dataRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Style headers
    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      let bgColor = '1565C0';
      if (index === 7) bgColor = '2E7D32';
      else if (index === 8) bgColor = 'E65100';
      else if (index === 9) bgColor = '7B1FA2';

      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: { bottom: { style: 'medium', color: { rgb: '000000' } } },
        };
      }
    });

    // Style data rows
    dataRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'E3F2FD' } },
            alignment: { horizontal: colIndex >= 7 ? 'right' : 'left', vertical: 'center' },
            numFmt: colIndex >= 7 ? '$#,##0.00' : undefined,
          };
        }
      });
    });

    // Style totals row
    const totalsRowIndex = dataRows.length + 1;
    totalsRow.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: totalsRowIndex, c: colIndex });
      let bgColor = '1565C0';
      if (colIndex === 7) bgColor = '2E7D32';
      else if (colIndex === 8) bgColor = 'E65100';
      else if (colIndex === 9) bgColor = '7B1FA2';

      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: colIndex >= 7 ? 'right' : (colIndex === 1 ? 'left' : 'center'), vertical: 'center' },
          numFmt: colIndex >= 7 ? '$#,##0.00' : undefined,
        };
      }
    });

    ws['!cols'] = [
      { wch: 6 }, { wch: 14 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Projects');
    XLSX.writeFile(wb, `Projects_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!filteredProjects || filteredProjects.length === 0) return;

    const doc = new jsPDF('landscape');

    doc.setFontSize(18);
    doc.setTextColor(21, 101, 192);
    doc.text('Project Management', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 28);

    const headers = [['SNo', 'Project Code', 'Project Name', 'PD', 'PM', 'Start Date', 'End Date', 'Total SOW', 'Burn Value', 'Remaining']];

    const data = filteredProjects.map((project, index) => [
      index + 1,
      project.project_code,
      project.name,
      project.pd_name || '-',
      project.pm_name || '-',
      project.sow_start_date || 'Not set',
      project.sow_end_date || 'Ongoing',
      `$${(project.total_sow_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${(project.burn_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${(project.remaining_sow_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    ]);

    // Calculate totals
    const totalSOW = filteredProjects.reduce((sum, p) => sum + (p.total_sow_value || 0), 0);
    const totalBurn = filteredProjects.reduce((sum, p) => sum + (p.burn_value || 0), 0);
    const totalRemaining = filteredProjects.reduce((sum, p) => sum + (p.remaining_sow_value || 0), 0);

    // Totals row for footer
    const footerData = [[
      '',
      'TOTAL',
      '',
      '',
      '',
      '',
      '',
      `$${totalSOW.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${totalBurn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `$${totalRemaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    ]];

    autoTable(doc, {
      head: headers,
      body: data,
      foot: footerData,
      startY: 35,
      theme: 'grid',
      headStyles: {
        fillColor: [21, 101, 192],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      footStyles: {
        fillColor: [21, 101, 192],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        7: { halign: 'right', fillColor: [232, 245, 233] },
        8: { halign: 'right', fillColor: [255, 243, 224] },
        9: { halign: 'right', fillColor: [243, 229, 245] },
      },
      alternateRowStyles: { fillColor: [227, 242, 253] },
      didParseCell: function(data) {
        // Style footer cells with specific colors
        if (data.section === 'foot') {
          if (data.column.index === 7) {
            data.cell.styles.fillColor = [46, 125, 50]; // Green for Total SOW
          } else if (data.column.index === 8) {
            data.cell.styles.fillColor = [230, 81, 0]; // Orange for Burn Value
          } else if (data.column.index === 9) {
            data.cell.styles.fillColor = [123, 31, 162]; // Purple for Remaining
          }
          data.cell.styles.halign = data.column.index >= 7 ? 'right' : 'left';
        }
      },
    });

    doc.save(`Projects_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Page Header */}
      <Box sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #1565C0 0%, #7C4DFF 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(21, 101, 192, 0.3)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Project Management</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Manage projects and SOW values
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Export to PDF">
              <Button
                size="small"
                variant="contained"
                startIcon={<PictureAsPdfIcon fontSize="small" />}
                onClick={exportToPDF}
                disabled={filteredProjects.length === 0}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                  fontSize: '0.75rem',
                }}
              >
                PDF
              </Button>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <Button
                size="small"
                variant="contained"
                startIcon={<TableChartIcon fontSize="small" />}
                onClick={exportToExcel}
                disabled={filteredProjects.length === 0}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                  fontSize: '0.75rem',
                }}
              >
                Excel
              </Button>
            </Tooltip>
            {canEdit && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleAddProject}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                  fontSize: '0.75rem',
                }}
              >
                Add
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 1, py: 0 }}>
          {success}
        </Alert>
      )}

      {/* Filter Section */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete
          freeSolo
          options={filterOptions.projectCodes}
          value={filterProjectCode}
          onChange={(e, newValue) => setFilterProjectCode(newValue || '')}
          onInputChange={(e, newValue) => setFilterProjectCode(newValue || '')}
          size="small"
          sx={{ minWidth: 140 }}
          renderInput={(params) => <TextField {...params} label="Project Code" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.projectNames}
          value={filterProjectName}
          onChange={(e, newValue) => setFilterProjectName(newValue || '')}
          onInputChange={(e, newValue) => setFilterProjectName(newValue || '')}
          size="small"
          sx={{ minWidth: 180 }}
          renderInput={(params) => <TextField {...params} label="Project Name" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.pdNames}
          value={filterPD}
          onChange={(e, newValue) => setFilterPD(newValue || '')}
          onInputChange={(e, newValue) => setFilterPD(newValue || '')}
          size="small"
          sx={{ minWidth: 130 }}
          renderInput={(params) => <TextField {...params} label="PD" />}
        />
        <Autocomplete
          freeSolo
          options={filterOptions.pmNames}
          value={filterPM}
          onChange={(e, newValue) => setFilterPM(newValue || '')}
          onInputChange={(e, newValue) => setFilterPM(newValue || '')}
          size="small"
          sx={{ minWidth: 130 }}
          renderInput={(params) => <TextField {...params} label="PM" />}
        />
        {(filterProjectCode || filterProjectName || filterPD || filterPM) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setFilterProjectCode('');
              setFilterProjectName('');
              setFilterPD('');
              setFilterPM('');
            }}
            sx={{ height: 40 }}
          >
            Clear
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: 40 }} align="center">#</TableCell>
              <TableCell sx={{ ...headerCellStyle, minWidth: 90 }}>Code</TableCell>
              <TableCell sx={{ ...headerCellStyle, minWidth: 120 }}>Project Name</TableCell>
              <TableCell sx={{ ...headerCellStyle, minWidth: 80 }}>PD</TableCell>
              <TableCell sx={{ ...headerCellStyle, minWidth: 80 }}>PM</TableCell>
              <TableCell sx={{ ...headerCellStyle, minWidth: 85 }}>Start</TableCell>
              <TableCell sx={{ ...headerCellStyle, minWidth: 85 }}>End</TableCell>
              <TableCell align="right" sx={{ ...headerCellStyle, backgroundColor: '#2E7D32', borderBottom: '2px solid #1B5E20', minWidth: 90 }}>SOW</TableCell>
              <TableCell align="right" sx={{ ...headerCellStyle, backgroundColor: '#E65100', borderBottom: '2px solid #BF360C', minWidth: 90 }}>Burn</TableCell>
              <TableCell align="right" sx={{ ...headerCellStyle, backgroundColor: '#7B1FA2', borderBottom: '2px solid #4A148C', minWidth: 90 }}>Balance</TableCell>
              {canEdit && <TableCell align="center" sx={{ ...headerCellStyle, width: 70 }}>Act</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 11 : 10} align="center" sx={{ py: 3 }}>
                  No projects found
                </TableCell>
              </TableRow>
            ) : (
              filteredProjects.map((project, index) => (
                <TableRow
                  key={project.project_code}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#E3F2FD',
                    '&:hover': { backgroundColor: '#BBDEFB' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell align="center" sx={compactCellStyle}>{index + 1}</TableCell>
                  <TableCell sx={{ ...compactCellStyle, fontWeight: 600 }}>{project.project_code}</TableCell>
                  <TableCell sx={compactCellStyle}>{project.name}</TableCell>
                  <TableCell sx={compactCellStyle}>{project.pd_name || '-'}</TableCell>
                  <TableCell sx={compactCellStyle}>{project.pm_name || '-'}</TableCell>
                  <TableCell sx={compactCellStyle}>{project.sow_start_date || '-'}</TableCell>
                  <TableCell sx={compactCellStyle}>{project.sow_end_date || '-'}</TableCell>
                  <TableCell align="right" sx={{ ...compactCellStyle, backgroundColor: index % 2 === 0 ? '#E8F5E9' : '#C8E6C9', fontWeight: 600 }}>
                    ${(project.total_sow_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right" sx={{ ...compactCellStyle, color: '#E65100', backgroundColor: index % 2 === 0 ? '#FFF3E0' : '#FFE0B2' }}>
                    ${(project.burn_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      ...compactCellStyle,
                      color: project.remaining_sow_value >= 0 ? '#2E7D32' : '#C62828',
                      fontWeight: 'bold',
                      backgroundColor: index % 2 === 0 ? '#F3E5F5' : '#E1BEE7',
                    }}
                  >
                    ${(project.remaining_sow_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="center" sx={{ ...compactCellStyle, padding: '4px' }}>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditProject(project.project_code)}
                        size="small"
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(project)}
                          size="small"
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
            {/* Totals Row */}
            {filteredProjects.length > 0 && (
              <TableRow sx={{ backgroundColor: '#1565C0' }}>
                <TableCell colSpan={7} sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'white', padding: '8px 6px' }}>
                  TOTAL ({filteredProjects.length} projects)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'white', backgroundColor: '#2E7D32', padding: '8px 6px' }}>
                  ${filteredProjects.reduce((sum, p) => sum + (p.total_sow_value || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'white', backgroundColor: '#E65100', padding: '8px 6px' }}>
                  ${filteredProjects.reduce((sum, p) => sum + (p.burn_value || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'white', backgroundColor: '#7B1FA2', padding: '8px 6px' }}>
                  ${filteredProjects.reduce((sum, p) => sum + (p.remaining_sow_value || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                {canEdit && <TableCell sx={{ backgroundColor: '#1565C0', padding: '8px 6px' }}></TableCell>}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Project"
        message="Are you sure you want to delete this project?"
        details={projectToDelete ? [
          { label: 'Project Code', value: projectToDelete.project_code },
          { label: 'Project Name', value: projectToDelete.name },
        ] : []}
      />
    </Container>
  );
};

export default ProjectList;
